<script setup lang="ts">
import { useFormDirtyStore } from '@/shared/composable/useFormDirty'
import { useConfirm } from '@/shared/composable/useConfirm'
import { useI18n } from 'vue-i18n'

export interface IItems {
  value: number
  label: string
}

defineProps<{
  items: IItems[]
}>()

const model = defineModel<number>()

const dirtyStore = useFormDirtyStore()
const { confirm } = useConfirm()
const { t } = useI18n()

async function handleClick(value: IItems['value']) {
  if (value === model.value) return

  if (dirtyStore.isDirty) {
    const ok = await confirm(
      t('common.confirmTitle'),
      t('common.unsavedChangesMessage'),
    )
    if (!ok) return
  }

  model.value = value
}
</script>

<template>
  <div class="border-b border-border skeleton-hide-border dark:border-white/10">
    <div class="flex gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar">
      <button
        v-for="option in items"
        :key="option.value"
        @click="handleClick(option.value)"
        class="px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-medium border-b-3 transition-all -mb-px cursor-pointer whitespace-nowrap shrink-0"
        :class="[
          model === option.value
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground dark:hover:text-white',
        ]"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
